import { css } from "glamor";
import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { colors, spacing } from "../library/theme";
import { createMockFromStudyPlan } from "../../processes/mockProccess";
import { formatDuration, formatMinutes } from "../../utils/studyPlanFormatters";
import { MOCK_MINUTES_PER_QUESTION } from "../../utils/generateStudyPlan";

const rowClass = css({
  display: "flex",
  alignItems: "center",
  gap: spacing.s,
  padding: `${spacing.s} ${spacing.m}`,
  borderRadius: 10,
  cursor: "pointer",
  backgroundColor: "#FFF8E1",
  border: "1px solid #FFE082",
  transition: "all 150ms ease",
  ":hover": {
    backgroundColor: "#FFF3C4",
    borderColor: "#FFD54F",
  },
});

const iconClass = css({
  width: 36,
  height: 36,
  borderRadius: 8,
  backgroundColor: "#FFE082",
  color: "#F57F17",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const infoClass = css({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const titleStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: colors.greyShadow,
  lineHeight: 1.3,
  margin: 0,
};

const durationClass = css({
  fontSize: 12,
  fontWeight: 500,
  color: colors.greyDarker,
  backgroundColor: "#FFE082",
  borderRadius: 6,
  padding: "2px 8px",
  flexShrink: 0,
});

const arrowClass = css({
  width: 28,
  height: 28,
  borderRadius: "50%",
  backgroundColor: "#FFE082",
  color: "#F57F17",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontSize: 12,
});

// ── modal styles ──────────────────────────────────────────────────────────────

const overlayClass = css({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
});

const modalClass = css({
  backgroundColor: "#fff",
  borderRadius: 12,
  padding: spacing.l,
  width: 380,
  maxWidth: "90vw",
  boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
});

const modalTitleStyle = {
  fontSize: 18,
  fontWeight: 700,
  color: colors.greyShadow,
  margin: `0 0 ${spacing.m} 0`,
};

const fieldLabelStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: colors.greyDarker,
  marginBottom: 4,
  display: "block",
};

const inputClass = css({
  width: "100%",
  padding: "8px 12px",
  fontSize: 16,
  border: `1px solid ${colors.greyLight}`,
  borderRadius: 8,
  outline: "none",
  boxSizing: "border-box",
  ":focus": {
    borderColor: colors.primary,
  },
});

const estimatedTimeStyle = {
  fontSize: 13,
  color: colors.greyDark,
  marginTop: 8,
  marginBottom: spacing.m,
};

const estimatedTimeBoldStyle = {
  fontWeight: 700,
  color: colors.greyShadow,
};

const buttonClass = css({
  width: "100%",
  padding: "10px 16px",
  fontSize: 15,
  fontWeight: 700,
  color: "#fff",
  backgroundColor: colors.primary,
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  transition: "opacity 150ms ease",
  ":hover": {
    opacity: 0.9,
  },
  ":disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
  },
});

const errorStyle = {
  fontSize: 13,
  color: "#D32F2F",
  marginTop: 8,
};

// eslint-disable-next-line no-unused-vars
export default function StudyPlanMockExamRow({ mock, overBudget }) {
  const dispatch = useDispatch();
  const maxQuestions = mock.maxQuestions || 140;
  const [open, setOpen] = useState(false);
  const [quantity, setQuantity] = useState(Math.min(10, maxQuestions));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const estimatedMinutes = quantity * MOCK_MINUTES_PER_QUESTION;

  const handleOpen = useCallback(() => {
    setOpen(true);
    setQuantity(Math.min(10, maxQuestions));
    setError(null);
  }, [maxQuestions]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleOpen();
    }
  }, [handleOpen]);

  function handleClose() {
    if (!loading) setOpen(false);
  }

  function handleQuantityChange(e) {
    const val = parseInt(e.target.value, 10);
    if (!isNaN(val)) {
      setQuantity(Math.max(1, Math.min(maxQuestions, val)));
    }
  }

  function handleSubmit() {
    setLoading(true);
    setError(null);

    createMockFromStudyPlan(dispatch, {
      courseId: mock.courseId,
      levelIds: mock.levelIds,
      quantity,
    })
      .then((data) => {
        window.location.href = "/mocks/" + data.id + "?study-plan=" + mock.courseId;
      })
      .catch(() => {
        setError("Erro ao criar simulado. Tente novamente.");
        setLoading(false);
      });
  }

  return (
    <>
      <div
        className={rowClass}
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={iconClass}>
          <i
            className="file alternate outline icon"
            style={{ margin: 0, lineHeight: 1, fontSize: 16 }}
          />
        </div>

        <div className={infoClass}>
          <p style={titleStyle}>
            Simulado - Módulo {mock.moduleNumber}
          </p>
        </div>

        <span className={durationClass}>
          {formatDuration(mock.durationSeconds)}
        </span>

        <div className={arrowClass}>
          <i
            className="arrow right icon"
            style={{ margin: 0, lineHeight: 1 }}
          />
        </div>
      </div>

      {open && (
        <div className={overlayClass} onClick={handleClose}>
          <div className={modalClass} onClick={(e) => e.stopPropagation()}>
            <h3 style={modalTitleStyle}>
              Simulado - Módulo {mock.moduleNumber}
            </h3>

            <label style={fieldLabelStyle}>
              Quantidade de questões (máx. {maxQuestions})
            </label>
            <input
              className={inputClass}
              type="number"
              min={1}
              max={maxQuestions}
              step={10}
              value={quantity}
              onChange={handleQuantityChange}
              disabled={loading}
            />

            <p style={estimatedTimeStyle}>
              Tempo estimado:{" "}
              <span style={estimatedTimeBoldStyle}>
                {formatMinutes(estimatedMinutes)}
              </span>
            </p>

            <button
              className={buttonClass}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Criando..." : "Iniciar simulado"}
            </button>

            {error && <p style={errorStyle}>{error}</p>}
          </div>
        </div>
      )}
    </>
  );
}
